import { FormGroup, IconName, Intent, Position } from '@blueprintjs/core';
import { DateInput3 } from '@blueprintjs/datetime2';
import moment from 'moment';
import React, { useEffect, useState } from 'react';

import PlatformFilterButton from '@components/PlatformFilterButton';
import PlatformListFilter from '@components/PlatformListFilter';
import { ToastUtils } from '@data/utils';

// DateInput3 is rendered without timePrecision, so its onChange contract is a bare calendar day
const CALENDAR_DAY_FORMAT = 'YYYY-MM-DD';
const CALENDAR_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Calendar day picked in the input -> value kept in state, null whenever it is not a bare calendar day
const parsePickedDay = (pickedDay: string | null): string | null => {
    if (pickedDay === null || !CALENDAR_DAY_PATTERN.test(pickedDay)) {
        return null;
    }

    const parsedDay = moment(pickedDay, CALENDAR_DAY_FORMAT, true);
    if (!parsedDay.isValid()) {
        return null;
    }

    return parsedDay.format(CALENDAR_DAY_FORMAT);
};

// Calendar day kept in state -> value the input expects
const toDateInputValue = (day: string | null): string | null => {
    if (day === null) {
        return null;
    }

    const parsedDay = moment(day, CALENDAR_DAY_FORMAT, true);
    if (!parsedDay.isValid()) {
        return null;
    }

    return parsedDay.format();
};

const formatDayLabel = (day: string) => moment(day, CALENDAR_DAY_FORMAT).format('MMM D, YYYY');

export interface PublishDateRangeFilterProps {
    defaultIsOpen?: boolean;
    icon?: IconName;

    // What the range is a range OF. Defaults to the publish date this filter was written for,
    // so every existing caller keeps its wording without passing anything
    title?: string;

    // Applied calendar days, both in 'YYYY-MM-DD'
    fromDate: string | null;
    toDate: string | null;

    onApply: (fromDate: string | null, toDate: string | null) => void;

    // Called after the values were cleared, whenever the chip itself may be removed
    onRemove?: () => void;
}

export type Props = PublishDateRangeFilterProps;

const PublishDateRangeFilter: React.FC<Props> = ({
    defaultIsOpen = false,
    icon = 'calendar',
    title = 'Publish Date',
    fromDate,
    toDate,
    onApply,
    onRemove,
}) => {
    const [isOpen, setIsOpen] = useState(defaultIsOpen);
    const [selectedFromDate, setSelectedFromDate] = useState<string | null>(fromDate);
    const [selectedToDate, setSelectedToDate] = useState<string | null>(toDate);

    useEffect(() => {
        // Applied values changed outside of the popover (hydration, reset), drop the draft
        setSelectedFromDate(fromDate);
        setSelectedToDate(toDate);
    }, [fromDate, toDate]);

    const getFilterTagLabel = () => {
        if (selectedFromDate !== null && selectedToDate !== null) {
            return `${formatDayLabel(selectedFromDate)} - ${formatDayLabel(selectedToDate)}`;
        }

        if (selectedFromDate !== null) {
            return `From ${formatDayLabel(selectedFromDate)}`;
        }

        if (selectedToDate !== null) {
            return `To ${formatDayLabel(selectedToDate)}`;
        }

        return '';
    };

    const getFilterTagTitle = () => {
        if (selectedFromDate === null && selectedToDate === null) {
            return title;
        }

        return `${title}:`;
    };

    const handleRemoveFilter = () => {
        setSelectedFromDate(null);
        setSelectedToDate(null);

        if (fromDate !== null || toDate !== null) {
            // Range was applied, so it has to be cleared
            onApply(null, null);
        }

        setIsOpen(false);

        onRemove?.();
    };

    const handleApplyFilter = () => {
        if (
            selectedFromDate !== null &&
            selectedToDate !== null &&
            moment(selectedFromDate, CALENDAR_DAY_FORMAT).isAfter(moment(selectedToDate, CALENDAR_DAY_FORMAT), 'day')
        ) {
            // Inverted range is never applied, otherwise it would silently show unfiltered results
            ToastUtils.showToast({
                message: '"From Date" must be on or before "To Date".',
                intent: Intent.WARNING,
            });

            // Drop the draft back to the applied range
            setSelectedFromDate(fromDate);
            setSelectedToDate(toDate);
            return;
        }

        if (selectedFromDate !== fromDate || selectedToDate !== toDate) {
            onApply(selectedFromDate, selectedToDate);
        }
    };

    const handleCloseFilter = () => {
        setIsOpen(false);
    };

    const renderDateInput = (
        value: string | null,
        onChange: (day: string | null) => void,
        placeholder: string,
        minDate: Date,
        maxDate: Date
    ) => {
        return (
            <DateInput3
                fill
                highlightCurrentDay
                canClearSelection
                placeholder={placeholder}
                value={toDateInputValue(value)}
                inputProps={{ leftIcon: 'calendar' }}
                minDate={minDate}
                maxDate={maxDate}
                dayPickerProps={{ firstWeekContainsDate: 1 }}
                formatDate={(date) => moment(date).format('MMM D, YYYY')}
                parseDate={(str) => new Date(str)}
                onChange={(newDate) => onChange(parsePickedDay(newDate))}
            />
        );
    };

    const renderRow = (key: number) => {
        // Opposite picker is constrained, so an inverted range can not be picked in the first place
        const lowerBoundary = moment('2018-01-01', CALENDAR_DAY_FORMAT);
        const upperBoundary = moment('2100-12-31', CALENDAR_DAY_FORMAT);
        const fromMaxDate = selectedToDate !== null ? moment(selectedToDate, CALENDAR_DAY_FORMAT) : upperBoundary;
        const toMinDate = selectedFromDate !== null ? moment(selectedFromDate, CALENDAR_DAY_FORMAT) : lowerBoundary;

        return (
            <li key={key}>
                <FormGroup className="mb-1" label="From Date:">
                    {renderDateInput(
                        selectedFromDate,
                        setSelectedFromDate,
                        'No lower bound',
                        lowerBoundary.toDate(),
                        fromMaxDate.toDate()
                    )}
                </FormGroup>

                <FormGroup className="mb-0" label="To Date:">
                    {renderDateInput(
                        selectedToDate,
                        setSelectedToDate,
                        'No upper bound',
                        toMinDate.toDate(),
                        upperBoundary.toDate()
                    )}
                </FormGroup>
            </li>
        );
    };

    return (
        <PlatformListFilter<number>
            removable
            isOpen={isOpen}
            title={title}
            position={Position.BOTTOM_LEFT}
            minWidth={260}
            list={[1]}
            itemRenderer={renderRow}
            onApply={handleApplyFilter}
            onClose={handleCloseFilter}
            onRemove={handleRemoveFilter}
        >
            <PlatformFilterButton
                icon={icon}
                onClick={() => setIsOpen(!isOpen)}
                label={getFilterTagLabel()}
                title={getFilterTagTitle()}
            />
        </PlatformListFilter>
    );
};

export default PublishDateRangeFilter;
